import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { clamp } from "lodash";
import {
  forwardRef,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { Box, VBox, VBoxProps } from "./Box";
import { useSize } from "./useSize";
import { mergeRefs } from "@/utils/reactHelpers";
import { BoxProps } from "./boxHelpers";

const KEYBOARD_RESIZE_STEP = 16;
const TRACK_WIDTH = 4;

type Side = "left" | "right";

type ResizeTrackProps = {
  side: Side;
  size: number;
  maxSize: number;
  isDragging: boolean;
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
};

const ResizeTrack = ({
  side,
  size,
  maxSize,
  isDragging,
  onPointerDown,
  onKeyDown,
}: ResizeTrackProps) => {
  const { t } = useTranslation();
  return (
    <div
      role={"separator"}
      aria-orientation={"vertical"}
      aria-label={t(
        side === "left"
          ? "resizable-layout.resize-left-panel"
          : "resizable-layout.resize-right-panel",
      )}
      aria-valuemin={0}
      aria-valuemax={maxSize}
      aria-valuenow={size}
      tabIndex={0}
      className={classNames(
        "z-1",
        "flex-none",
        "cursor-col-resize",
        "touch-none",
        "select-none",
        "hover:bg-primary-light",
        "focus-visible:bg-primary-light",
        isDragging && "bg-primary-light",
      )}
      style={{ width: TRACK_WIDTH }}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );
};

export type ResizableSidepanelLayoutProps = {
  leftPanel?: ReactNode;
  centerPanel?: ReactNode;
  rightPanel?: ReactNode;
  leftBaseSize?: number;
  leftBaseSizePercent?: number;
  rightBaseSize?: number;
  rightBaseSizePercent?: number;
  leftPanelContainerProps?: VBoxProps;
  centerPanelContainerProps?: VBoxProps;
  rightPanelContainerProps?: VBoxProps;
  containerProps?: BoxProps;
};

type DragState = { side: Side; startX: number; startSize: number };

const dragDirection = (side: Side) => (side === "left" ? 1 : -1);

const percentOfWidth = (percent: number | undefined, width: number) =>
  percent !== undefined && width > 0 ? (width * percent) / 100 : undefined;

const maxShareOfAvailableWidth = (percent: number | undefined) => (percent === undefined ? 0.5 : 1);

const capToAvailableWidth = (
  size: number,
  otherSideSize: number,
  availableWidth: number,
  maxShare: number,
) => (availableWidth > 0 ? clamp(size, 0, (availableWidth - otherSideSize) * maxShare) : size);

export const ResizableSidepanelLayout = forwardRef<HTMLDivElement, ResizableSidepanelLayoutProps>(
  function ResizableSidepanelLayout(
    {
      leftPanel,
      centerPanel,
      rightPanel,
      leftBaseSize = 280,
      leftBaseSizePercent,
      rightBaseSize = 280,
      rightBaseSizePercent,
      leftPanelContainerProps,
      centerPanelContainerProps,
      rightPanelContainerProps,
      containerProps,
    },
    ref,
  ) {
    const [leftUserSize, setLeftUserSize] = useState<number | null>(null);
    const [rightUserSize, setRightUserSize] = useState<number | null>(null);
    const [drag, setDrag] = useState<DragState | null>(null);
    const containerSize = useSize({ measureParent: true });
    const leftPanelSize = useSize({ measureParent: false });
    const rightPanelSize = useSize({ measureParent: false });

    const containerWidth = containerSize.size.width;
    const availableWidth =
      containerWidth - (leftPanel ? TRACK_WIDTH : 0) - (rightPanel ? TRACK_WIDTH : 0);
    const leftBase = percentOfWidth(leftBaseSizePercent, containerWidth) ?? leftBaseSize;
    const rightBase = percentOfWidth(rightBaseSizePercent, containerWidth) ?? rightBaseSize;
    const leftInitialSize = capToAvailableWidth(
      leftBase,
      rightPanel ? rightBase : 0,
      availableWidth,
      maxShareOfAvailableWidth(leftBaseSizePercent),
    );
    const rightInitialSize = capToAvailableWidth(
      rightBase,
      leftPanel ? leftInitialSize : 0,
      availableWidth,
      maxShareOfAvailableWidth(rightBaseSizePercent),
    );
    const leftSize = leftUserSize ?? leftInitialSize;
    const rightSize = rightUserSize ?? rightInitialSize;

    const leftMaxSize =
      containerSize.size.width - (rightPanel ? rightPanelSize.size.width + TRACK_WIDTH : 0);
    const rightMaxSize =
      containerSize.size.width - (leftPanel ? leftPanelSize.size.width + TRACK_WIDTH : 0);

    useEffect(() => {
      if (drag) {
        const maxSize = drag.side === "left" ? leftMaxSize : rightMaxSize;
        const setSize = drag.side === "left" ? setLeftUserSize : setRightUserSize;
        const handleMove = (e: PointerEvent) =>
          setSize(
            clamp(
              drag.startSize + dragDirection(drag.side) * (e.clientX - drag.startX),
              0,
              maxSize,
            ),
          );
        const handleUp = () => setDrag(null);
        window.addEventListener("pointermove", handleMove);
        window.addEventListener("pointerup", handleUp);
        return () => {
          window.removeEventListener("pointermove", handleMove);
          window.removeEventListener("pointerup", handleUp);
        };
      }
    }, [drag, leftMaxSize, rightMaxSize]);

    const startDrag = (side: Side) => (e: ReactPointerEvent<HTMLDivElement>) =>
      setDrag({ side, startX: e.clientX, startSize: side === "left" ? leftSize : rightSize });

    const resizeByKeyboard = (side: Side) => (e: KeyboardEvent<HTMLDivElement>) => {
      const towardRight = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (towardRight) {
        e.preventDefault();
        const size = side === "left" ? leftSize : rightSize;
        const maxSize = side === "left" ? leftMaxSize : rightMaxSize;
        const setSize = side === "left" ? setLeftUserSize : setRightUserSize;
        setSize(clamp(size + dragDirection(side) * towardRight * KEYBOARD_RESIZE_STEP, 0, maxSize));
      }
    };

    return (
      <Box
        ref={mergeRefs(ref, containerSize.setRef)}
        gap={0}
        grow
        {...containerProps}
        className={classNames(containerProps?.className, "max-w-full")}
      >
        {leftPanel && (
          <>
            <VBox
              ref={leftPanelSize.setRef}
              flex={"none"}
              overflowY={"auto"}
              borderRight
              bg
              gap={0}
              {...leftPanelContainerProps}
              className={classNames(leftPanelContainerProps?.className, "relative")}
              htmlProps={{
                ...leftPanelContainerProps?.htmlProps,
                style: {
                  ...leftPanelContainerProps?.htmlProps?.style,
                  minWidth: 0,
                  width: leftSize,
                },
              }}
            >
              {leftPanel}
            </VBox>
            <ResizeTrack
              side={"left"}
              size={leftSize}
              maxSize={leftMaxSize}
              isDragging={drag?.side === "left"}
              onPointerDown={startDrag("left")}
              onKeyDown={resizeByKeyboard("left")}
            />
          </>
        )}
        <VBox
          grow
          overflowY={"auto"}
          gap={0}
          flex="auto"
          {...centerPanelContainerProps}
          className={classNames(centerPanelContainerProps?.className, "min-w-0")}
        >
          {centerPanel}
        </VBox>
        {rightPanel && (
          <>
            <ResizeTrack
              side={"right"}
              size={rightSize}
              maxSize={rightMaxSize}
              isDragging={drag?.side === "right"}
              onPointerDown={startDrag("right")}
              onKeyDown={resizeByKeyboard("right")}
            />
            <VBox
              ref={rightPanelSize.setRef}
              flex={"none"}
              overflowY={"auto"}
              borderLeft
              bg
              gap={0}
              {...rightPanelContainerProps}
              htmlProps={{
                ...rightPanelContainerProps?.htmlProps,
                style: {
                  ...rightPanelContainerProps?.htmlProps?.style,
                  width: rightSize,
                  minWidth: 0,
                  maxWidth: clamp(
                    rightSize,
                    TRACK_WIDTH,
                    containerSize.size.width - (leftPanel ? leftSize + TRACK_WIDTH : 0),
                  ),
                },
              }}
            >
              {rightPanel}
            </VBox>
          </>
        )}
      </Box>
    );
  },
);
